import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { themeActions } from "../actions/theme.actions";
import { map, switchMap, tap, withLatestFrom } from "rxjs";
import { Store } from "@ngrx/store";
import { ThemeState } from "app/core/models/theme-settings.interface";
import { defaultTheme } from "../reducers/theme.reducers";
import * as fromTheme from '../reducers';
import { ThemeService } from "app/core/settings/theme.service";


const STORAGE_KEY = 'themeState';

@Injectable()
export class ThemeEffects {

    constructor(
        private actions$: Actions,
        private store: Store,
        private themeService: ThemeService
    ) { }

    loadTheme$ = createEffect(() =>
        this.actions$.pipe(
            ofType(themeActions.loadTheme),
            map(() => {
                const stored = localStorage.getItem(STORAGE_KEY);
                const parsed = stored ? (JSON.parse(stored) as ThemeState) : defaultTheme;
                return themeActions.loadThemeSuccess({ state: { ...defaultTheme, ...parsed } });
            })
        )
    );

    saveTheme$ = createEffect(() =>
        this.actions$.pipe(
            ofType(themeActions.saveTheme),
            withLatestFrom(this.store.select(fromTheme.selectTheme)),
            map(([{ profile, mode }, current]) => {
                const base: ThemeState = { ...defaultTheme, ...(current ?? {}) };

                const updatedProfiles: ThemeState['profiles'] = {
                    ...base.profiles,
                    [mode]: {
                        ...base.profiles[mode],
                        ...profile,
                    },
                };

                const updated: ThemeState = {
                    ...base,
                    mode: base.mode,
                    profiles: updatedProfiles,
                };

                try {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
                    return themeActions.saveThemeSuccess({ state: updated });
                } catch {
                    return themeActions.saveThemeFailure();
                }
            })
        )
    );

    updateProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(themeActions.updateProfile),
            withLatestFrom(this.store.select(fromTheme.selectTheme)),
            map(([{ changes, mode }, current]) => {
                const base: ThemeState = { ...defaultTheme, ...(current ?? {}) };

                const activeMode = mode ?? base.mode;

                const updatedProfiles: ThemeState['profiles'] = {
                    ...base.profiles,
                    [activeMode]: {
                        ...base.profiles[activeMode],
                        ...changes,
                    },
                };

                const updated: ThemeState = {
                    ...base,
                    mode: activeMode,
                    profiles: updatedProfiles,
                };

                if (changes.primaryColor) {
                    this.themeService.changePrimaryColorByKey(changes.primaryColor);
                }
                if (changes.surfaceColor) {
                    this.themeService.changeSurfaceColor(changes.surfaceColor);
                }

                return themeActions.updateProfileSuccess({ state: updated });
            })
        )
    );

    resetToDefault$ = createEffect(() =>
        this.actions$.pipe(
            ofType(themeActions.resetToDefault),
            withLatestFrom(this.store.select(fromTheme.selectTheme)),
            map(([_, current]) => {
                const base: ThemeState = { ...defaultTheme, ...(current ?? {}) };
                const updated: ThemeState = {
                    ...base,
                    profiles: {
                        ...base.profiles,
                        [base.mode]: defaultTheme.profiles[base.mode],
                    },
                };

                return themeActions.resetToDefaultSuccess({ state: updated });
            })
        )
    );

    applyTheme$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                themeActions.loadThemeSuccess,
                themeActions.updateProfileSuccess,
                themeActions.resetToDefaultSuccess,
            ),
            withLatestFrom(this.store.select(fromTheme.selectTheme)),
            map(([_, theme]) => {
                if (!theme) return themeActions.saveThemeFailure();

                const s: ThemeState = theme;
                const p = s.profiles[s.mode];

                document.documentElement.classList.toggle('dark-mode', s.mode === 'dark');
                this.themeService.changeDarkMode(s.mode === 'dark');
                this.themeService.changePrimaryColorByKey(p.primaryColor);
                this.themeService.changeSurfaceColor(p.surfaceColor);
                this.themeService.configureToolbar(s.mode === 'dark');
                this.themeService.setFont(p.font);
                this.themeService.changeMaterial(p.material);

                return themeActions.saveTheme({ profile: p, mode: s.mode });
            })
        )
    );
}